import {useCallback, useEffect, useRef, useState} from "react";

const clamp = (value: number, lo: number, hi: number) =>
    Math.min(hi, Math.max(lo, value));

type Options = {
    /** localStorage key the size is persisted under. */
    storageKey: string;
    /** Initial width of the leading pane, in pixels. */
    defaultSize: number;
    /** Smallest / largest the leading pane may become, in pixels. */
    min: number;
    max: number;
    /** Keyboard nudge per arrow press, in pixels. */
    step?: number;
};

/**
 * Horizontal split sizing for two panes: the leading pane gets a fixed pixel
 * `size`, the trailing pane takes the rest (flex/`1fr`). Sizes are pixels — not
 * percentages — so the leading pane stays put when the window resizes and only
 * the trailing pane grows/shrinks. The width is persisted per `storageKey`.
 *
 * Deliberately not a general N-pane solver — one handle resizes its two
 * neighbours, which is all the docgen workspace needs. Spread `separatorProps`
 * onto the drag handle and put `containerRef` on the flex row / grid.
 */
export function useResizable({
    storageKey,
    defaultSize,
    min,
    max,
    step = 24,
}: Options) {
    const containerRef = useRef<HTMLDivElement>(null);

    const [size, setSize] = useState<number>(() => {
        const stored = Number(localStorage.getItem(storageKey));
        return Number.isFinite(stored) && stored > 0
            ? clamp(stored, min, max)
            : defaultSize;
    });

    useEffect(() => {
        localStorage.setItem(storageKey, String(size));
    }, [storageKey, size]);

    const sizeFromClientX = useCallback(
        (clientX: number) => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return;
            // Leading-pane width = pointer offset from the container's left edge.
            setSize(clamp(clientX - rect.left, min, max));
        },
        [min, max]
    );

    const onPointerDown = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            document.body.style.userSelect = "none";
            document.body.style.cursor = "col-resize";
        },
        []
    );

    const onPointerMove = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
            sizeFromClientX(e.clientX);
        },
        [sizeFromClientX]
    );

    const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
    }, []);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            const nudge: Record<string, number> = {
                ArrowLeft: -step,
                ArrowRight: step,
            };
            if (e.key in nudge) {
                setSize((s) => clamp(s + nudge[e.key], min, max));
                e.preventDefault();
            } else if (e.key === "Home") {
                setSize(min);
                e.preventDefault();
            } else if (e.key === "End") {
                setSize(max);
                e.preventDefault();
            }
        },
        [min, max, step]
    );

    return {
        containerRef,
        size,
        separatorProps: {
            role: "separator",
            "aria-orientation": "vertical",
            "aria-valuenow": Math.round(size),
            "aria-valuemin": min,
            "aria-valuemax": max,
            tabIndex: 0,
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onKeyDown,
            onDoubleClick: () => setSize(defaultSize),
        } as const,
    };
}
